<template>
  <div class="full-height" style="overflow: hidden">
    <Map ref="dashboardMap" :tools="tools" :zoom="zoom" :center="center" :style="{ width: '100%' }">
      <v-card flat style="position: absolute; bottom: 10px; left: 10px; z-index: 2" class="px-2 py-1">
        <span style="font-weight: 600">Total: {{ total }}</span>
      </v-card>
    </Map>
  </div>
</template>
<script>
import Map from '@/components/map/Map.vue'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import GeoJSON from 'ol/format/GeoJSON'
import TileLayer from 'ol/layer/Tile'
import TileWMS from 'ol/source/TileWMS.js'
import { XYZ } from 'ol/source'
import VectorTileLayer from 'ol/layer/VectorTile'
import VectorTileSource from 'ol/source/VectorTile.js'
import MVT from 'ol/format/MVT.js'
import { transformExtent } from 'ol/proj'
import randomMaterialColor from 'random-material-color'
import { DEFAULT_TYPE, DISPLAY_TYPES } from '@/constants/map'
import { getChangeDetectionSets } from '@/api/change-detection'

export default {
  props: {
    wards: [],
    total: Number,
  },
  components: { Map },
  data() {
    return {
      zoom: 11,
      center: [94.36464, 26.52479],

      tools: [
        {
          name: 'Scale',
          position: { bottom: '10px', right: '220px' },
        },
        {
          name: 'MousePosition',
          position: { bottom: '10px', right: '10px' },
        },
        {
          name: 'Zoom',
          position: { bottom: '50px', right: '10px' },
        },
        {
          name: 'ExtentControl',
          position: { bottom: '105px', right: '10px' },
        },
        {
          name: 'TileSwitcher',
          position: { bottom: '83px', right: '42px', size: '45' },
        },
        {
          name: 'Geolocation',
          position: { bottom: '50px', right: '42px' },
        },
        {
          name: 'FullScreen',
          position: { bottom: '50px', right: '74px' },
        },
        {
          name: 'Measure',
          position: { bottom: '50px', right: '106px' },
        },
      ],
    }
  },

  methods: {
    createDefaultStyle(type, color) {
      const displayType = DISPLAY_TYPES.find(t => t.name === DEFAULT_TYPE[type])
      const style = JSON.parse(JSON.stringify(displayType.styles).replaceAll(/\$color/g, color)).reduce(
        (s, c) => ({ ...{ [c.key]: c.default }, ...s }),
        {},
      )
      return { color, style, displayType: displayType.name }
    },

    getFeaturesFromGeoJSON(geojson, dataProjection = 'EPSG:4326', featureProjection = 'EPSG:3857') {
      return new GeoJSON().readFeatures(geojson, { dataProjection, featureProjection })
    },

    createRasterLayer(extent, minZoom, maxZoom, url) {
      const source = new XYZ({ url, maxZoom, minZoom })
      const rasterLayer = new TileLayer({ extent, source })
      rasterLayer.setProperties({ color: 'var(--v-primary-base)' })
      return rasterLayer
    },

    createVectorLayer(extent, minZoom, maxZoom, tileUrl, vectorType) {
      const source = new VectorTileSource({ format: new MVT(), url: tileUrl, minZoom, maxZoom })
      const mapLayer = new VectorTileLayer({ renderMode: 'hybrid', extent, source, declutter: true })
      const { style } = this.createDefaultStyle(vectorType, randomMaterialColor.getColor())
      mapLayer.setStyle(style)
      return mapLayer
    },

    createWMSLayer(extent, url, credentials, layerName) {
      const source = new TileWMS({
        url,
        params: { LAYERS: layerName, TILED: true, FORMAT: 'image/png' },
        serverType: 'geoserver',
        transition: 0,
        tileLoadFunction: (imageTile, src) => {
          const client = new XMLHttpRequest()
          client.responseType = 'blob'
          client.open('GET', src)
          if (credentials) {
            client.setRequestHeader('Authorization', `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`)
          }
          client.onload = () => {
            const objectUrl = URL.createObjectURL(client.response)
            const img = imageTile.getImage()
            img.addEventListener('load', function () {
              URL.revokeObjectURL(objectUrl)
            })
            img.src = objectUrl
          }
          client.send()
        },
      })
      return new TileLayer({ extent, source })
    },

    createWFSLayer(geojson) {
      const features = this.getFeaturesFromGeoJSON(geojson, 'EPSG:3857')
      const mapLayer = new VectorLayer({ source: new VectorSource({ features }) })
      const { style } = this.createDefaultStyle(
        features[0].getGeometry().getType().toString(),
        randomMaterialColor.getColor(),
      )
      mapLayer.setStyle(style)
      return mapLayer
    },

    createResultLayer(geojson, vectorType) {
      const mapLayer = new VectorLayer({ source: new VectorSource({ features: this.getFeaturesFromGeoJSON(geojson) }) })
      const { style } = this.createDefaultStyle(vectorType, randomMaterialColor.getColor())
      mapLayer.setStyle(style)
      return mapLayer
    },

    async loadBoundaryLayer() {
      const { data: cdSets } = await getChangeDetectionSets({ sortBy: 'id:desc', with: 'boundaryLayer' })
      const layer = cdSets[0]?.boundaryLayer
      if (!layer) return

      const { extent, minZoom, maxZoom, tileUrl, vectorType, geojson } = layer
      const transformedExtent = extent ? transformExtent(extent, 'EPSG:4326', 'EPSG:3857') : null
      let mapLayer
      switch (layer.type) {
        case 'raster':
          mapLayer = this.createRasterLayer(transformedExtent, minZoom, maxZoom, tileUrl)
          break
        case 'vector':
        case 'complaint':
        case 'plot':
          mapLayer = this.createVectorLayer(transformedExtent, minZoom, maxZoom, tileUrl, vectorType)
          break
        case 'wms':
          mapLayer = this.createWMSLayer(transformedExtent, tileUrl, layer.credentials, layer.layer)
          break
        case 'wfs':
          mapLayer = this.createWFSLayer(geojson)
          break
        case 'result':
          mapLayer = this.createResultLayer(geojson, vectorType)
          break
        default:
          return
      }
      mapLayer.setProperties({ id: layer.id, name: layer.name })
      this.$refs.dashboardMap.map.addLayer(mapLayer)

      const isEmptyExtent = extent && extent.every(v => v === 0)
      if (transformedExtent && !isEmptyExtent) {
        this.$refs.dashboardMap.map.getView().fit(transformedExtent, { padding: [20, 20, 20, 20] })
      }
    },
  },

  async mounted() {
    await this.loadBoundaryLayer()
  },
}
</script>